<template>
  <snackbar
    ref="snackbar"
    base-size="100px"
    :hold-time="5000"
    :multiple="true"
    :position="'bottom-center'"
    :colors="colors"
  />
</template>

<script lang="ts">
import { reactive } from "vue";

export const messageState = reactive({
  message: null,

  openMessage(text, keepOpen = false) {
    const requestId = Math.floor(Math.random() * 100000);

    this.message = null;
    setTimeout(() => {
      this.message = {
        requestId: requestId,
        text: text,
        type: "open",
        keepOpen: keepOpen
      };
    });
    return requestId;
  },

  infoMessage(text, keepOpen = false) {
    const requestId = Math.floor(Math.random() * 1000000);

    this.message = null;
    setTimeout(() => {
      this.message = {
        requestId: requestId,
        text: text,
        type: "info",
        keepOpen: keepOpen
      };
    });
    return requestId;
  },

  warnMessage(text, keepOpen = false) {
    const requestId = Math.floor(Math.random() * 1000000);

    this.message = null;
    setTimeout(() => {
      this.message = {
        requestId: requestId,
        text: text,
        type: "warn",
        keepOpen: keepOpen
      };
    });
    return requestId;
  },

  errorMessage(text, keepOpen = false) {
    const requestId = Math.floor(Math.random() * 1000000);

    this.message = null;
    setTimeout(() => {
      this.message = {
        requestId: requestId,
        text: text && text !== "" ? text : "An error occured", // TODO Translate
        type: "error",
        keepOpen: keepOpen
      };
    });
    return requestId;
  },

  updateMessage(requestId, text) {
    this.message = {
      requestId: requestId,
      text: text,
      type: "update"
    };
  },

  closeMessage(requestId) {
    this.message = {
      requestId: requestId,
      type: "close"
    };
  }
});
</script>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Snackbar from "@/components/Shared/snackbar.vue";

import { StringHelper } from "@/helpers/utilities/StringHelper";
import { useGlobalsStore } from "@/stores/globals";

defineOptions({ name: "GlobalSnackbar" });

const { t } = useI18n();
const globalsStore = useGlobalsStore();

const snackbar = ref<any>(null);
const requestMap: Record<string, number> = {}; // requestId: snackbarId

const colors = computed(() => ({
  warning: "#F4642D",
  error: "var(--color-danger)",
  info: "var(--color-primary-light)"
}));

const localizeMessage = message => {
  if (message.startsWith("$t(")) {
    return t(StringHelper.cleanForTranslationTag(message));
  }

  return message;
};

const parseMessage = message => {
  if (Array.isArray(message)) {
    return message
      .map(m => {
        if (m.toString().startsWith("$t(")) {
          return t(StringHelper.cleanForTranslationTag(m));
        } else {
          return m;
        }
      })
      .join(" ");
  }

  return localizeMessage(message);
};

watch(
  () => messageState.message,
  message => {
    if (message) {
      switch (message.type) {
        case "error":
          requestMap[message.requestId] = snackbar.value.addError(parseMessage(message.text), message.keepOpen);
          break;

        case "warn":
          requestMap[message.requestId] = snackbar.value.addWarning(parseMessage(message.text), message.keepOpen);
          break;

        case "update":
          snackbar.value.update(requestMap[message.requestId], parseMessage(message.text));
          break;

        case "close":
          snackbar.value.remove(requestMap[message.requestId]);
          break;

        default:
          requestMap[message.requestId] = snackbar.value.addInfo(parseMessage(message.text), message.keepOpen);
          break;
      }
    }
  }
);

watch(
  () => globalsStore.infoMessage,
  message => {
    if (message) {
      snackbar.value.addInfo(parseMessage(message));
      globalsStore.setInfoMessage(null);
    }
  }
);

watch(
  () => globalsStore.errorMessage,
  message => {
    if (message) {
      snackbar.value.addError(parseMessage(message));
      globalsStore.setErrorMessage(null);
    }
  }
);
</script>
